// Stub for native-only modules (react-native-reanimated, react-native-unity2,
// react-native-keyboard-controller, react-native-gesture-handler, etc.) used
// by the web bundle. The default Proxy export returns no-ops for any property
// access so transitive consumers don't crash on `Animated.createAnimatedComponent(...)`,
// `KeyboardController.setEnabled(...)`, etc. Web never renders these libs; they
// only matter on RN. Named exports below provide shape-correct stubs for the
// specific hooks/components that GiftedChat actually consumes — returning a
// Proxy from `useSharedValue(0)` would yield a Proxy in `.value`, which then
// breaks `useAnimatedStyle({ opacity: proxy })` style validation.
import {
  Text as RNText,
  TextInput as RNTextInput,
  View as RNViewImport,
  ScrollView as RNScrollViewImport,
  FlatList as RNFlatListImport,
  Pressable as RNPressable,
  TouchableOpacity as RNTouchableOpacity,
  TouchableHighlight as RNTouchableHighlight,
  TouchableWithoutFeedback as RNTouchableWithoutFeedback,
} from 'react-native';

const noop = () => null;
const handler = {
  get(_t, prop) {
    if (prop === '__esModule') return true;
    return new Proxy(noop, handler);
  },
  apply() {
    return new Proxy({}, handler);
  },
};
const stub = new Proxy(noop, handler);

// react-native-reanimated hook stubs — return shapes that satisfy library expectations
// SharedValue has .value plus .modify/.addListener/.removeListener — libs call all of them
function makeSharedValueLike(initial) {
  return {
    value: initial,
    modify(fn) { if (typeof fn === 'function') { try { this.value = fn(this.value); } catch {} } return this.value; },
    addListener() {},
    removeListener() {},
    get() { return this.value; },
  };
}
export function useSharedValue(initial) {
  return makeSharedValueLike(initial);
}
export function useAnimatedStyle(fn) {
  try { return typeof fn === 'function' ? fn() : {}; } catch { return {}; }
}
export function useDerivedValue(fn) {
  return makeSharedValueLike(typeof fn === 'function' ? fn() : fn);
}
export function useAnimatedScrollHandler() {
  return () => {};
}
export function useAnimatedRef() {
  return { current: null };
}
export function useAnimatedReaction() { /* no-op — RN-only animation reaction */ }
export function useAnimatedKeyboard() { return { state: { value: 0 }, height: { value: 0 } }; }
export function useFrameCallback() { return { setActive: () => {} }; }
export function useScrollViewOffset() { return { value: 0 }; }
export function useEvent() { return () => {}; }
export function defineAnimation(_, fn) { return typeof fn === 'function' ? fn() : fn; }
export function withTiming(toValue) { return toValue; }
export function withSpring(toValue) { return toValue; }
export function withDelay(_, value) { return value; }
export function withSequence(...args) { return args[args.length - 1]; }
export function withRepeat(value) { return value; }
export function runOnJS(fn) { return fn; }
export function runOnUI(fn) { return fn; }
export function interpolate(_value, _input, output) { return Array.isArray(output) ? output[0] : 0; }
export function interpolateColor(_value, _input, output) { return Array.isArray(output) ? output[0] : '#000'; }
export const Easing = new Proxy({}, { get: () => () => 0 });
export const Extrapolate = { CLAMP: 'clamp', EXTEND: 'extend', IDENTITY: 'identity' };
export const Extrapolation = { CLAMP: 'clamp', EXTEND: 'extend', IDENTITY: 'identity' };

// Entering/exiting transitions — used by some libs as JSX prop values.
// Each is a chainable builder; we return passthrough objects.
const transitionBuilder = () => {
  const o = {};
  ['duration', 'delay', 'easing', 'springify', 'damping', 'mass', 'stiffness',
   'overshootClamping', 'restDisplacementThreshold', 'restSpeedThreshold',
   'withInitialValues', 'reduceMotion', 'build'].forEach(k => { o[k] = () => o; });
  return o;
};
export const FadeIn = transitionBuilder();
export const FadeOut = transitionBuilder();
export const SlideInDown = transitionBuilder();
export const SlideOutDown = transitionBuilder();
export const SlideInUp = transitionBuilder();
export const SlideOutUp = transitionBuilder();
export const ZoomIn = transitionBuilder();
export const ZoomOut = transitionBuilder();
export const Layout = transitionBuilder();
export const LinearTransition = transitionBuilder();
export const Keyframe = function() { return transitionBuilder(); };
// Module-init-time SharedValue creators (NOT hooks) — must return { value }-shaped objects
export function makeMutable(initial) { return { value: initial }; }
export function makeShareable(value) { return value; }
export function cancelAnimation() { /* no-op */ }
export function measure() { return { x: 0, y: 0, width: 0, height: 0, pageX: 0, pageY: 0 }; }
export function scrollTo() { /* no-op */ }
// Animated component factory — returns the input component as-is (no animation on web)
export const createAnimatedComponent = (Component) => Component;

// Default reanimated namespace export. `import Animated from 'react-native-reanimated'`
// gets this object; `<Animated.View>` resolves to RNView (renders fine on web).
export const Animated = {
  View: RNViewImport,
  Text: RNText,
  ScrollView: RNScrollViewImport,
  FlatList: RNFlatListImport,
  Image: RNViewImport,
  createAnimatedComponent,
};

// react-native-keyboard-controller stubs — these need to render children
const PassthroughComponent = ({ children }) => children ?? null;
export const KeyboardProvider = PassthroughComponent;
export const KeyboardAvoidingView = PassthroughComponent;
export const KeyboardController = { setDefaultMode: () => {}, dismiss: () => {} };
export const useKeyboardHandler = () => {};
export const useReanimatedKeyboardAnimation = () => ({ height: { value: 0 }, progress: { value: 0 } });

// react-native-gesture-handler stubs — needed because GiftedChat imports
// Text/TextInput/Pressable/BaseButton from RNGH. These MUST be real
// interactive RN components (not passthroughs) — otherwise onPress / onLongPress
// are dropped and the Send button silently does nothing.
export const Text = RNText;
export const TextInput = RNTextInput;
export const Pressable = RNPressable;
export const TouchableOpacity = RNTouchableOpacity;
export const TouchableHighlight = RNTouchableHighlight;
export const TouchableWithoutFeedback = RNTouchableWithoutFeedback;
export const BaseButton = RNPressable;
export const RectButton = RNPressable;
export const BorderlessButton = RNPressable;
export const ScrollView = RNScrollViewImport;
export const FlatList = RNFlatListImport;
export const State = { UNDETERMINED: 0, FAILED: 1, BEGAN: 2, CANCELLED: 3, ACTIVE: 4, END: 5 };
export const GestureHandlerRootView = PassthroughComponent;
export const Directions = { RIGHT: 1, LEFT: 2, UP: 4, DOWN: 8 };
export const Gesture = { Pan: () => ({ enabled: () => ({}) }), Tap: () => ({ enabled: () => ({}) }) };
export const GestureDetector = PassthroughComponent;

// View — use the real RN View so layout + flex actually work
export const View = RNViewImport;

// Default export must satisfy both `<Animated />` (component) AND `<Animated.View>` (namespace).
// Implemented as a passthrough functional component with attached properties for the namespace.
const AnimatedDefault = ({ children }) => children ?? null;
AnimatedDefault.View = RNViewImport;
AnimatedDefault.Text = RNText;
AnimatedDefault.ScrollView = RNScrollViewImport;
AnimatedDefault.FlatList = RNFlatListImport;
AnimatedDefault.Image = RNViewImport;
AnimatedDefault.createAnimatedComponent = createAnimatedComponent;
export default AnimatedDefault;
